/**
 * @file apps/web/src/lib/navGates.ts
 * @description Pure lock logic for the project sidebar (AppShell's
 * ProjectNav), pulled out of the component so it can be unit-tested without a
 * DOM: given the latest GET /projects/:id/overview data, is a nav item locked?
 * A phase-gated item is locked until its gating phase reports 'complete';
 * UAT and Launch additionally consult their approval gates. With no overview
 * loaded yet, every gated item is locked.
 */

export interface NavPhaseStatus {
  phase:  'discovery' | 'design' | 'build' | 'test' | 'close';
  status: 'locked' | 'in_progress' | 'complete';
}

export interface NavOverview {
  phases:     NavPhaseStatus[];
  uatGate:    { unlocked: boolean };
  launchGate: { unlocked: boolean };
}

export interface NavGateSpec {
  gatePhase:  NavPhaseStatus['phase'] | null; // null = never locked
  extraGate?: 'uat' | 'launch';
}

export function isPhaseComplete(overview: NavOverview | null, phase: NavPhaseStatus['phase']): boolean {
  return (overview?.phases ?? []).find((p) => p.phase === phase)?.status === 'complete';
}

export function isNavItemLocked(overview: NavOverview | null, item: NavGateSpec): boolean {
  return (item.gatePhase !== null && !isPhaseComplete(overview, item.gatePhase))
    || (item.extraGate === 'uat'    && !!overview && !overview.uatGate.unlocked)
    || (item.extraGate === 'launch' && !!overview && !overview.launchGate.unlocked);
}
